"use client"

import { type ComponentProps, useState } from "react"
import { Box } from "~/components/legacy/common/box"
import { ExternalLink } from "~/components/web/external-link"
import { cx } from "~/lib/legacy-utils"

type OverlayImageProps = ComponentProps<typeof ExternalLink> & {
  src: string
  alt?: string
  loading?: ComponentProps<"img">["loading"]
  fallbackLabel?: string
}

export const OverlayImage = (props: OverlayImageProps) => (
  <OverlayImageContent key={props.src} {...props} />
)

function OverlayImageContent({
  className,
  src,
  alt,
  loading,
  fallbackLabel,
  ...props
}: OverlayImageProps) {
  const [failed, setFailed] = useState(false)
  return (
    <Box hover>
      <ExternalLink
        className={cx("not-prose group relative rounded-lg overflow-clip", className)}
        doTrack
        {...props}
      >
        {failed ? (
          <div
            role="img"
            aria-label={fallbackLabel ? `Icon of ${fallbackLabel}` : "Website preview"}
            className="flex aspect-video w-full flex-col items-center justify-center gap-3 bg-linear-to-br from-primary/10 via-background to-primary/5 text-primary"
          >
            <span className="flex size-20 items-center justify-center rounded-2xl border border-primary/15 bg-background/80 text-4xl font-semibold shadow-sm">
              {Array.from(fallbackLabel?.trim() || "?")[0].toUpperCase()}
            </span>
            {fallbackLabel && <span className="text-sm font-medium">{fallbackLabel}</span>}
          </div>
        ) : (
          <img
            src={src}
            alt={alt ?? ""}
            width={1280}
            height={1024}
            loading={loading}
            onError={() => setFailed(true)}
            onLoad={event => {
              if (event.currentTarget.naturalWidth <= 1 || event.currentTarget.naturalHeight <= 1) {
                setFailed(true)
              }
            }}
            className="aspect-video h-auto w-full object-cover object-top will-change-transform group-hover:scale-[101%]"
          />
        )}
      </ExternalLink>
    </Box>
  )
}
